/*
 * Copyright Red Hat, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type {
  CacheService,
  LoggerService,
} from '@backstage/backend-plugin-api';
import type { Config } from '@backstage/config';
import {
  DefaultGithubCredentialsProvider,
  GithubCredentials,
  GithubIntegrationConfig,
  ScmIntegrations,
} from '@backstage/integration';

import { Octokit } from '@octokit/rest';
import gitUrlParse from 'git-url-parse';

import { getBranchName, getCatalogFilename } from '../catalog/catalogUtils';
import {
  computeTotalCount,
  extractLocationOwnerMap,
  logErrorIfNeeded,
} from '../helpers';
import { GitApiService } from '../scm/GitApiService';
import {
  DefaultPageNumber,
  DefaultPageSize,
} from '../service/handlers/handlers';
import { CustomGithubCredentialsProvider } from './GithubAppManager';
import {
  isGithubAppCredential,
  type ExtendedGithubCredentials,
  type GithubFetchError,
  type GithubOrganization,
  type GithubOrganizationResponse,
  type GithubRepository,
  type GithubRepositoryResponse,
} from './types';
import { buildOcto } from './utils/ghUtils';
import {
  addGithubAppOrgs,
  addGithubTokenOrgs,
  getAllAppOrgs,
} from './utils/orgUtils';
import {
  closePRWithComment,
  findOpenPRForBranch,
  getCatalogInfoContentFromPR,
} from './utils/prUtils';
import {
  addGithubAppRepositories,
  addGithubTokenOrgRepositories,
  addGithubTokenRepositories,
  fileExistsInDefaultBranch,
  type ValidatedRepo,
} from './utils/repoUtils';
import {
  executeFunctionOnFirstSuccessfulIntegration,
  fetchFromAllIntegrations,
  fetchFromMatchedIntegration,
} from './utils/utils';

export class GithubApiService implements GitApiService {
  private readonly logger: LoggerService;
  private readonly integrations: ScmIntegrations;
  public readonly githubCredentialsProvider: CustomGithubCredentialsProvider;
  private readonly config: Config;
  // Cache for storing ETags (used for efficient caching of unchanged data returned by GitHub)
  private readonly cache: CacheService;

  constructor(
    logger: LoggerService,
    config: Config,
    cacheService: CacheService,
  ) {
    this.logger = logger;
    this.config = config;
    this.integrations = ScmIntegrations.fromConfig(config);
    this.githubCredentialsProvider =
      CustomGithubCredentialsProvider.fromIntegrations(this.integrations);
    this.cache = cacheService;
  }

  private get integrationDeps() {
    return {
      logger: this.logger,
      cache: this.cache,
      githubCredentialsProvider: this.githubCredentialsProvider,
    };
  }

  private get executionDeps() {
    return {
      ...this.integrationDeps,
      config: this.config,
    };
  }

  private buildRepositoryResponse(
    repositories: Map<string, GithubRepository>,
    result: { data: number[]; errors: Map<number, GithubFetchError> },
    pageSize: number,
  ): GithubRepositoryResponse {
    const repoList = Array.from(repositories.values());
    const totalCount = computeTotalCount(repoList, result.data, pageSize);
    return {
      repositories: repoList,
      errors: Array.from(result.errors.values()),
      totalCount,
    };
  }

  async getCredentials(repoUrl: string): Promise<{ token: string }> {
    const provider = DefaultGithubCredentialsProvider.fromIntegrations(
      this.integrations,
    );
    const creds = await provider.getCredentials({
      url: repoUrl,
    });
    if (!creds || !creds.token) {
      throw new Error(`Token not configured for 'github' provider`);
    }
    return {
      token: creds.token,
    };
  }

  async getRepositoryFromIntegrations(repoUrl: string): Promise<{
    repository?: GithubRepository;
    errors?: GithubFetchError[];
  }> {
    const { data, errors } = await fetchFromMatchedIntegration(
      this.integrationDeps,
      this.integrations,
      repoUrl,
      async (octokit, gitUrl) => {
        const resp = await octokit.rest.repos.get({
          owner: gitUrl.owner,
          repo: gitUrl.name,
        });
        const repo = resp?.data;
        if (!repo) {
          return undefined;
        }
        return {
          name: repo.name,
          full_name: repo.full_name,
          url: repo.url,
          html_url: repo.html_url,
          default_branch: repo.default_branch,
          updated_at: repo.updated_at,
        };
      },
    );

    return {
      repository: data,
      errors,
    };
  }

  async getOrganizationsFromIntegrations(
    pageNumber: number = DefaultPageNumber,
    pageSize: number = DefaultPageSize,
    search?: string,
  ): Promise<GithubOrganizationResponse> {
    const orgs = new Map<string, GithubOrganization>();
    const result = await fetchFromAllIntegrations(
      this.integrationDeps,
      this.integrations,
      {
        dataFetcher: async (
          octokit: Octokit,
          credential: ExtendedGithubCredentials,
          ghConfig: GithubIntegrationConfig,
        ) => {
          const dataFetchErrors = new Map<number, GithubFetchError>();
          const resp = isGithubAppCredential(credential)
            ? await addGithubAppOrgs(
                {
                  logger: this.logger,
                  githubCredentialsProvider: this.githubCredentialsProvider,
                },
                octokit,
                ghConfig,
                {
                  credentialAccountLogin: credential.accountLogin,
                  search,
                  orgs,
                  errors: dataFetchErrors,
                },
              )
            : await addGithubTokenOrgs(
                {
                  logger: this.logger,
                },
                octokit,
                credential,
                {
                  search,
                  orgs,
                  pageNumber,
                  pageSize,
                  errors: dataFetchErrors,
                },
              );

          this.logger.debug(
            `Got ${resp.totalCount} org(s) for ${ghConfig.host}`,
          );
          return {
            result: resp.totalCount ?? 0,
            errors: Array.from(dataFetchErrors.values()),
          };
        },
      },
    );

    const orgList = Array.from(orgs.values());
    const totalCount = computeTotalCount(orgList, result.data, pageSize);
    return {
      organizations: orgList,
      errors: Array.from(result.errors?.values() ?? []),
      totalCount,
    };
  }

  private async fetchUserTokenGithubIntegrationRepositories(
    userTokens: Record<string, string>,
    fetcher: (
      octokit: Octokit,
      credential: GithubCredentials,
      errors: Map<number, GithubFetchError>,
    ) => Promise<{ totalCount?: number }>,
  ): Promise<{ errors: GithubFetchError[]; totalCount: number | undefined }> {
    const allErrors: GithubFetchError[] = [];
    let totalCount: number | undefined;
    const ghConfigs = this.integrations.github.list().map(i => i.config);
    for (const ghConfig of ghConfigs) {
      const hostUrl = `https://${ghConfig.host}`;
      const token = userTokens[hostUrl];
      if (!token) {
        continue;
      }
      // Synthesize a token credential — same shape buildOcto already accepts
      const userCredential: GithubCredentials = { token, type: 'token' };

      // Intentionally no cache: user-token requests must not share the
      // server-credential ETag cache to avoid cross-user data leakage.
      const userOctokit = buildOcto(
        { logger: this.logger, cache: undefined },
        { credential: userCredential },
        ghConfig.apiBaseUrl,
      );
      if (!userOctokit) {
        continue;
      }
      const dataFetchErrors = new Map<number, GithubFetchError>();
      const result = await fetcher(
        userOctokit,
        userCredential,
        dataFetchErrors,
      );
      totalCount = (totalCount ?? 0) + (result.totalCount ?? 0);
      dataFetchErrors.forEach(err => allErrors.push(err));
    }
    return { errors: allErrors, totalCount };
  }

  async getOrgRepositoriesFromIntegrations(
    orgName: string,
    search?: string,
    pageNumber: number = DefaultPageNumber,
    pageSize: number = DefaultPageSize,
    userTokens?: Record<string, string>,
  ): Promise<GithubRepositoryResponse> {
    const repositories = new Map<string, GithubRepository>();

    // When a user token is present, build a user-scoped Octokit per integration
    // config and call addGithubTokenOrgRepositories with it. This scopes the
    // listing to repositories the user has access to within the given organization.
    // When absent, fall through to the existing server-credential path.
    if (userTokens && Object.keys(userTokens).length > 0) {
      const { errors: allErrors, totalCount } =
        await this.fetchUserTokenGithubIntegrationRepositories(
          userTokens,
          (userOctokit, userCredential, dataFetchErrors) =>
            addGithubTokenOrgRepositories(
              { logger: this.logger },
              userOctokit,
              userCredential,
              orgName,
              repositories,
              dataFetchErrors,
              { search, pageNumber, pageSize },
            ),
        );
      const repoList = Array.from(repositories.values());
      return { repositories: repoList, errors: allErrors, totalCount };
    }

    const result = await fetchFromAllIntegrations(
      this.integrationDeps,
      this.integrations,
      {
        dataFetcher: async (
          octokit: Octokit,
          credential: ExtendedGithubCredentials,
          ghConfig: GithubIntegrationConfig,
        ) => {
          const dataFetchErrors = new Map<number, GithubFetchError>();
          let resp: { totalCount?: number };
          if (isGithubAppCredential(credential)) {
            if (credential.accountLogin !== orgName) {
              return {};
            }
            resp = await addGithubAppRepositories(
              {
                logger: this.logger,
                githubCredentialsProvider: this.githubCredentialsProvider,
              },
              octokit,
              credential,
              repositories,
              dataFetchErrors,
              {
                search,
              },
            );
          } else {
            resp = await addGithubTokenOrgRepositories(
              {
                logger: this.logger,
              },
              octokit,
              credential,
              orgName,
              repositories,
              dataFetchErrors,
              {
                search,
              },
            );
          }
          this.logger.debug(
            `Got ${resp.totalCount} org repo(s) for ${ghConfig.host}`,
          );
          return {
            stopFetchingData: true,
            result: resp.totalCount ?? 0,
            errors: Array.from(dataFetchErrors.values()),
          };
        },
      },
    );

    return this.buildRepositoryResponse(repositories, result, pageSize);
  }

  /**
   * Returns GithubRepositoryResponse containing:
   *   - a list of unique repositories the github integrations have access to
   *   - a list of errors encountered by each app and/or token (if any exist)
   */
  async getRepositoriesFromIntegrations(
    search?: string,
    userTokens?: Record<string, string>,
  ): Promise<GithubRepositoryResponse> {
    const repositories = new Map<string, GithubRepository>();

    // When a user token is present, build a user-scoped Octokit per integration
    // config and call addGithubTokenRepositories with it. This makes
    // listForAuthenticatedUser return only repos the user personally has access to.
    // When absent, fall through to the existing server-credential path.
    if (userTokens && Object.keys(userTokens).length > 0) {
      const { errors: allErrors, totalCount } =
        await this.fetchUserTokenGithubIntegrationRepositories(
          userTokens,
          (userOctokit, userCredential, dataFetchErrors) =>
            addGithubTokenRepositories(
              { logger: this.logger },
              userOctokit,
              userCredential,
              repositories,
              dataFetchErrors,
              { search },
            ),
        );
      const repoList = Array.from(repositories.values());
      return { repositories: repoList, errors: allErrors, totalCount };
    }

    const result = await fetchFromAllIntegrations(
      this.integrationDeps,
      this.integrations,
      {
        dataFetcher: async (
          octokit: Octokit,
          credential: ExtendedGithubCredentials,
          ghConfig: GithubIntegrationConfig,
        ) => {
          const dataFetchErrors = new Map<number, GithubFetchError>();
          const resp = isGithubAppCredential(credential)
            ? await addGithubAppRepositories(
                {
                  logger: this.logger,
                  githubCredentialsProvider: this.githubCredentialsProvider,
                },
                octokit,
                credential,
                repositories,
                dataFetchErrors,
                {
                  search,
                },
              )
            : await addGithubTokenRepositories(
                {
                  logger: this.logger,
                },
                octokit,
                credential,
                repositories,
                dataFetchErrors,
                {
                  search,
                },
              );
          this.logger.debug(
            `Got ${resp.totalCount} repo(s) for ${ghConfig.host}`,
          );
          return {
            result: resp.totalCount ?? 0,
            errors: Array.from(dataFetchErrors.values()),
          };
        },
      },
    );

    return this.buildRepositoryResponse(repositories, result, DefaultPageSize);
  }

  async filterLocationsAccessibleFromIntegrations(
    locationUrls: string[],
  ): Promise<string[]> {
    const locationGitOwnerMap = extractLocationOwnerMap(locationUrls);

    const allAccessibleAppOrgs = new Set<string>();
    const allAccessibleTokenOrgs = new Set<string>();
    const allAccessibleUsernames = new Set<string>();
    await fetchFromAllIntegrations(this.integrationDeps, this.integrations, {
      dataFetcher: async (
        octokit: Octokit,
        credential: ExtendedGithubCredentials,
        ghConfig: GithubIntegrationConfig,
      ) => {
        if (isGithubAppCredential(credential)) {
          const appOrgMap = await getAllAppOrgs(
            this.githubCredentialsProvider,
            ghConfig,
            credential.accountLogin,
          );
          for (const [_, ghOrg] of appOrgMap) {
            allAccessibleAppOrgs.add(ghOrg.name);
          }
        } else {
          // find authenticated GitHub owner...
          const username = (await octokit.rest.users.getAuthenticated())?.data
            ?.login;
          if (username) {
            allAccessibleUsernames.add(username);
          }
          // ... along with orgs accessible from the token auth
          (await octokit.paginate(octokit.rest.orgs.listForAuthenticatedUser))
            ?.map(org => org.login)
            ?.forEach(orgName => allAccessibleTokenOrgs.add(orgName));
        }
        return {};
      },
    });

    return locationUrls.filter(loc => {
      if (!locationGitOwnerMap.has(loc)) {
        return false;
      }
      const owner = locationGitOwnerMap.get(loc)!;
      return (
        allAccessibleAppOrgs.has(owner) ||
        allAccessibleTokenOrgs.has(owner) ||
        allAccessibleUsernames.has(owner)
      );
    });
  }

  async getPullRequest(
    repoUrl: string,
    pullRequestNumber: number,
  ): Promise<{
    title?: string;
    body?: string;
    merged?: boolean;
    lastUpdated?: string;
    prSha?: string;
    prBranch?: string;
  }> {
    const { data } = await fetchFromMatchedIntegration(
      this.integrationDeps,
      this.integrations,
      repoUrl,
      async (octokit, gitUrl) => {
        try {
          const resp = await octokit.rest.pulls.get({
            owner: gitUrl.owner,
            repo: gitUrl.name,
            pull_number: pullRequestNumber,
          });
          const pr = resp.data;
          return {
            title: pr.title,
            body: pr.body ?? undefined,
            merged: pr.merged,
            lastUpdated: pr.updated_at,
            prSha: pr.head.sha,
            prBranch: pr.head.ref,
          };
        } catch (error: any) {
          logErrorIfNeeded(this.logger, 'Error fetching pull requests', error);
          return undefined;
        }
      },
    );
    return data ?? {};
  }

  async updatePullRequest(
    repoUrl: string,
    pullRequestNumber: number,
    title?: string,
    body?: string,
  ): Promise<void> {
    await fetchFromMatchedIntegration(
      this.integrationDeps,
      this.integrations,
      repoUrl,
      async (octokit, gitUrl) => {
        try {
          await octokit.rest.pulls.update({
            owner: gitUrl.owner,
            repo: gitUrl.name,
            pull_number: pullRequestNumber,
            title: title,
            body: body,
          });
          return;
        } catch (error: any) {
          logErrorIfNeeded(this.logger, 'Error updating pull requests', error);
          throw error;
        }
      },
    );
  }

  async createOrUpdateFileInBranch(
    owner: string,
    repo: string,
    branchName: string,
    fileName: string,
    fileContent: string,
  ): Promise<void> {
    try {
      const ghConfig = this.integrations.github.byUrl(
        `https://github.com/${owner}/${repo}`,
      )?.config;
      if (!ghConfig) {
        throw new Error(
          `Could not find GH integration from https://github.com/${owner}/${repo}`,
        );
      }

      const credentials =
        await this.githubCredentialsProvider.getAllCredentials({
          host: ghConfig.host,
        });

      if (credentials.length === 0) {
        throw new Error(`No credentials for GH integration`);
      }

      for (const credential of credentials) {
        const octokit = buildOcto(
          {
            logger: this.logger,
            cache: this.cache,
          },
          { credential, owner },
          ghConfig.apiBaseUrl,
        );
        if (!octokit) {
          continue;
        }

        try {
          const { data: existingFile } = await octokit.rest.repos.getContent({
            owner: owner,
            repo: repo,
            path: fileName,
            ref: branchName,
          });
          // Response can either be a directory (array of files) or a single file element. In this case, we ensure it has the sha property to update it.
          if (Array.isArray(existingFile) || !('sha' in existingFile)) {
            throw new Error(
              `The content at path ${fileName} is not a file or the response from GitHub does not contain the 'sha' property.`,
            );
          }
          // If the file already exists, update it
          await octokit.rest.repos.createOrUpdateFileContents({
            owner,
            repo,
            path: fileName,
            message: `Add ${fileName} config file`,
            content: btoa(fileContent),
            sha: existingFile.sha,
            branch: branchName,
          });
        } catch (error: any) {
          if (error.status === 404) {
            // If the file does not exist, create it
            await octokit.rest.repos.createOrUpdateFileContents({
              owner,
              repo,
              path: fileName,
              message: `Add ${fileName} config file`,
              content: btoa(fileContent),
              branch: branchName,
            });
          } else {
            throw error;
          }
        }
      }
    } catch (error: any) {
      this.logger.error(
        `Error creating or updating file in branch ${branchName}`,
        error,
      );
      throw error;
    }
  }

  async findImportOpenPr(
    logger: LoggerService,
    input: {
      repoUrl: string;
      includeCatalogInfoContent?: boolean;
    },
  ): Promise<{
    prNum?: number;
    prUrl?: string;
    prTitle?: string;
    prBody?: string;
    prCatalogInfoContent?: string;
    lastUpdate?: string;
  }> {
    const branchName = getBranchName(this.config);
    const { data } = await fetchFromMatchedIntegration(
      this.integrationDeps,
      this.integrations,
      input.repoUrl,
      async (octokit, gitUrl) => {
        try {
          return await findOpenPRForBranch(
            logger,
            this.config,
            octokit,
            gitUrl.organization,
            gitUrl.name,
            branchName,
            input.includeCatalogInfoContent,
          );
        } catch (error: any) {
          logErrorIfNeeded(this.logger, 'Error fetching pull requests', error);
          return undefined;
        }
      },
    );
    return data ?? {};
  }

  async getCatalogInfoFile(
    logger: LoggerService,
    input: {
      repoUrl: string;
      prNumber: number;
      prHeadSha: string;
    },
  ): Promise<string | undefined> {
    const { data } = await fetchFromMatchedIntegration(
      this.integrationDeps,
      this.integrations,
      input.repoUrl,
      async (octokit, gitUrl) => {
        try {
          return await getCatalogInfoContentFromPR(
            logger,
            this.config,
            octokit,
            gitUrl.organization,
            gitUrl.name,
            input.prNumber,
            input.prHeadSha,
          );
        } catch (error: any) {
          logErrorIfNeeded(
            this.logger,
            'Error fetching catalog info file content requests',
            error,
          );
          return undefined;
        }
      },
    );
    return data;
  }

  async submitPrToRepo(
    logger: LoggerService,
    input: {
      repoUrl: string;
      gitUrl: gitUrlParse.GitUrl;
      defaultBranch?: string;
      prTitle: string;
      prBody: string;
      catalogInfoContent: string;
    },
  ): Promise<{
    prUrl?: string;
    prNumber?: number;
    hasChanges?: boolean;
    lastUpdate?: string;
    errors?: string[];
  }> {
    const fileName = getCatalogFilename(this.config);
    const errors: any[] = [];

    const result = await executeFunctionOnFirstSuccessfulIntegration(
      this.executionDeps,
      this.integrations,
      {
        repoUrl: input.repoUrl,
        fn: async (
          validatedRepo: ValidatedRepo,
          octo: Octokit,
        ): Promise<{
          successful: boolean;
          result?: {
            prUrl?: string;
            prNumber?: number;
            hasChanges?: boolean;
            lastUpdate?: string;
          };
        }> => {
          const { owner, repo, branchName } = validatedRepo;
          try {
            // Check if there is already a catalogInfo in the default branch
            const catalogInfoFileExists = await fileExistsInDefaultBranch(
              logger,
              octo,
              owner,
              repo,
              fileName,
              input.defaultBranch,
            );
            if (catalogInfoFileExists) {
              // No need to create a PR => component will be imported as is
              return {
                successful: true,
                result: {
                  hasChanges: false,
                },
              };
            }

            const existingPrForBranch = await findOpenPRForBranch(
              logger,
              this.config,
              octo,
              owner,
              repo,
              branchName,
            );

            const repoData = await octo.rest.repos.get({
              owner,
              repo,
            });
            const parentRef = await octo.rest.git.getRef({
              owner,
              repo,
              ref: `heads/${repoData.data.default_branch}`,
            });
            if (existingPrForBranch.prNum) {
              await this.createOrUpdateFileInBranch(
                owner,
                repo,
                branchName,
                fileName,
                input.catalogInfoContent,
              );
              const pullRequestResponse = await octo.rest.pulls.update({
                owner,
                repo,
                pull_number: existingPrForBranch.prNum,
                title: input.prTitle,
                body: input.prBody,
                head: branchName,
                base: repoData.data.default_branch,
              });
              return {
                successful: true,
                result: {
                  prNumber: existingPrForBranch.prNum,
                  prUrl: pullRequestResponse.data.html_url,
                  lastUpdate: pullRequestResponse.data.updated_at,
                },
              };
            }

            let branchExists = false;
            try {
              await octo.rest.git.getRef({
                owner,
                repo,
                ref: `heads/${branchName}`,
              });
              branchExists = true;
            } catch (error: any) {
              if (error.status === 404) {
                await octo.rest.git.createRef({
                  owner,
                  repo,
                  ref: `refs/heads/${branchName}`,
                  sha: parentRef.data.object.sha,
                });
              } else {
                throw error;
              }
            }

            if (branchExists) {
              // update it in case it is outdated compared to the base branch
              try {
                await octo.repos.merge({
                  owner: owner,
                  repo: repo,
                  base: branchName,
                  head: repoData.data.default_branch,
                });
              } catch (error: any) {
                logErrorIfNeeded(
                  this.logger,
                  `Could not merge default branch ${repoData.data.default_branch} into import branch ${branchName}`,
                  error,
                );
              }
            }

            await this.createOrUpdateFileInBranch(
              owner,
              repo,
              branchName,
              fileName,
              input.catalogInfoContent,
            );

            const pullRequestResponse = await octo.rest.pulls.create({
              owner,
              repo,
              title: input.prTitle,
              body: input.prBody,
              head: branchName,
              base: repoData.data.default_branch,
            });
            return {
              successful: true,
              result: {
                prNumber: pullRequestResponse.data.number,
                prUrl: pullRequestResponse.data.html_url,
                lastUpdate: pullRequestResponse.data.updated_at,
                hasChanges: true,
              },
            };
          } catch (e: any) {
            logErrorIfNeeded(
              this.logger,
              `Couldn't create PR in ${input.repoUrl}`,
              e,
            );
            errors.push(e.message);
            return { successful: false };
          }
        },
      },
    );

    if (result) {
      return result;
    }

    logger.warn(
      `Tried all possible GitHub credentials, but could not create PR in ${input.repoUrl}. Please try again later...`,
    );

    return {
      errors: errors,
    };
  }

  async hasFileInRepo(input: {
    repoUrl: string;
    defaultBranch?: string;
    fileName: string;
  }): Promise<boolean> {
    const fileExists = await executeFunctionOnFirstSuccessfulIntegration(
      this.executionDeps,
      this.integrations,
      {
        repoUrl: input.repoUrl,
        fn: async (validatedRepo: ValidatedRepo, octo: Octokit) => {
          const { owner, repo } = validatedRepo;
          const exists = await fileExistsInDefaultBranch(
            this.logger,
            octo,
            owner,
            repo,
            input.fileName,
            input.defaultBranch,
          );
          if (exists === undefined) {
            return { successful: false };
          }
          return { successful: true, result: exists };
        },
      },
    );

    if (fileExists === undefined) {
      throw new Error(
        `Could not determine if repo at ${input.repoUrl} already has a file named ${input.fileName} in its default branch (${input.defaultBranch})`,
      );
    }

    return fileExists;
  }

  async closeImportPR(
    logger: LoggerService,
    input: {
      repoUrl: string;
      gitUrl: gitUrlParse.GitUrl;
      comment: string;
    },
  ): Promise<void> {
    await executeFunctionOnFirstSuccessfulIntegration(
      this.executionDeps,
      this.integrations,
      {
        repoUrl: input.repoUrl,
        fn: async (validatedRepo: ValidatedRepo, octo: Octokit) => {
          const { owner, repo, branchName } = validatedRepo;
          try {
            const existingPrForBranch = await findOpenPRForBranch(
              logger,
              this.config,
              octo,
              owner,
              repo,
              branchName,
            );
            if (existingPrForBranch.prNum) {
              await closePRWithComment(
                octo,
                owner,
                repo,
                existingPrForBranch.prNum,
                input.comment,
              );
            }
            return { successful: true };
          } catch (e: any) {
            logErrorIfNeeded(
              this.logger,
              `Couldn't close PR in ${input.repoUrl}`,
              e,
            );
            return { successful: false };
          }
        },
      },
    );
  }

  async deleteImportBranch(input: {
    repoUrl: string;
    gitUrl: gitUrlParse.GitUrl;
  }): Promise<void> {
    await executeFunctionOnFirstSuccessfulIntegration(
      this.executionDeps,
      this.integrations,
      {
        repoUrl: input.repoUrl,
        fn: async (validatedRepo: ValidatedRepo, octo: Octokit) => {
          const { owner, repo, branchName } = validatedRepo;
          try {
            await octo.git.deleteRef({
              owner: owner,
              repo: repo,
              ref: `heads/${branchName}`,
            });
            return { successful: true };
          } catch (e: any) {
            logErrorIfNeeded(
              this.logger,
              `Couldn't close import PR and/or delete import branch in ${input.repoUrl}`,
              e,
            );
            return { successful: false };
          }
        },
      },
    );
  }

  async isRepoEmpty(input: { repoUrl: string }): Promise<boolean | undefined> {
    return await executeFunctionOnFirstSuccessfulIntegration(
      this.executionDeps,
      this.integrations,
      {
        repoUrl: input.repoUrl,
        fn: async (validatedRepo: ValidatedRepo, octo: Octokit) => {
          const { owner, repo } = validatedRepo;
          const resp = await octo.rest.repos.listContributors({
            owner: owner,
            repo: repo,
            page: 1,
            per_page: 1,
          });
          const status = resp.status as 200 | 204;
          return { successful: true, result: status === 204 };
        },
      },
    );
  }
}
